import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { currencyFormat } from '../utils/numbers'
import { formatPhoneNumber } from '../utils/phone'
import { BaseRecord } from './BaseRecord'
import { CacheFactory } from './CacheFactory'
import { Customizable } from './Customizable'
import { Image } from './Image'
import { Industry } from './Industry'
import { Option } from './Option'
import { RecordDataMap } from './RecordDataMap'
import { RecordFactory } from './RecordFactory'
import { FKType } from './SQLBuilder'
import { Type } from './Type'
import { User } from './User'

export type FieldConstructorArgs = Omit<
    ConstructorArgs<() => Field>,
    'required' | 'client' | 'booking' | 'location' | 'order' | 'subaccount' | 'options'
> & {
    is_required: boolean | null
    is_client: boolean | null
    is_booking: boolean | null
    is_location: boolean | null
    is_order: boolean | null
    is_subaccount: boolean | null
    options: Option[] | { value: string; label: string; seqnum: number }[] | null
    labels: string[]
}

export enum FIELD_TYPE {
    FIELD_TYPE_TEXT,
    FIELD_TYPE_EMAIL,
    FIELD_TYPE_DATE,
    FIELD_TYPE_TIME,
    FIELD_TYPE_RANGE,
    FIELD_TYPE_DATETIME,
    FIELD_TYPE_PHONE,
    FIELD_TYPE_URL,
    FIELD_TYPE_FLOAT,
    FIELD_TYPE_INTEGER,
    FIELD_TYPE_PERCENT,
    FIELD_TYPE_CURRENCY,
    FIELD_TYPE_PASSWORD,
    FIELD_TYPE_SWITCH,
    FIELD_TYPE_LABEL,
    FIELD_TYPE_LONGTEXT,
    FIELD_TYPE_LOCATION,
    FIELD_TYPE_USERNAME,
    //list
    FIELD_TYPE_SELECT,
    FIELD_TYPE_MULTISELECT,
    FIELD_TYPE_CREDIT_CARD,
    FIELD_TYPE_FIELD,
    FIELD_TYPE_ITEM,
    FIELD_TYPE_PRODUCT,
    FIELD_TYPE_GIFT,
    FIELD_TYPE_GIFT_CERTIFICATE,
    FIELD_TYPE_SERVICE,
    FIELD_TYPE_PACKAGE,
    FIELD_TYPE_SUBSCRIPTION,
    FIELD_TYPE_BOOKABLE_ITEM,
    FIELD_TYPE_ONLINE_ITEM,
    FIELD_TYPE_INDUSTRY,
    FIELD_TYPE_FEATURE,
    FIELD_TYPE_CONTRACT,
    FIELD_TYPE_TEMPLATE,
    FIELD_TYPE_FORM,
    FIELD_TYPE_USER,
    FIELD_TYPE_CLIENT,
    FIELD_TYPE_CLIENT_OR_SUBACCOUNT,
    FIELD_TYPE_EMPLOYEE,
    FIELD_TYPE_ANY_EMPLOYEE_OR_CONTRACTOR,
    FIELD_TYPE_PAYEE,
    FIELD_TYPE_MARKETING_CHANNEL,
    FIELD_TYPE_SUMMARY,
}

export class Field extends BaseRecord {
    readonly recordType = 'field'

    name?: string
    standardField?: string
    label?: string
    type?: string
    seqnum?: number
    expires?: string
    edition?: string
    editionField?: string
    client?: boolean
    booking?: boolean
    order?: boolean
    location?: boolean
    subaccount?: boolean
    isPublic?: boolean
    required?: boolean
    checkoutOther?: boolean
    checkoutOrder?: boolean
    checkoutClient?: boolean
    checkoutBooking?: boolean
    checkoutLocation?: boolean
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment
    options: Option[] = []

    static FIELD_CACHE_KEY_PREFIX = 'field'
    static FIELD_TYPES_ARRAY = [
        'text',
        'integer',
        'select',
        'multiselect',
        'checkbox',
        'date',
        'float',
        'currency',
        'longtext',
        'email',
        'phone',
        'image',
        'document',
    ]
    static FIELD_TYPES = new Map([
        ['text', 'Text'],
        ['integer', 'Integer'],
        ['select', 'Single choice'],
        ['multiselect', 'Multiple choice'],
        ['checkbox', 'Checkbox'],
        ['date', 'Date'],
        ['float', 'Decimal number'],
        ['currency', 'Currency'],
        ['longtext', 'Long text'],
        ['email', 'Email'],
        ['phone', 'Phone number'],
        ['image', 'Image'],
        ['document', 'File upload'],
    ])

    constructor(
        data?: Partial<FieldConstructorArgs>,
        params?: { name?: string; label?: string; type?: string }
    ) {
        super(data)
        if (!data) {
            this.name = params?.name
            this.type = params?.type
            this.label = params?.label
            this.isPublic = true
            this.options = []
            this.creator = PSApplication.me()
            return
        }

        this.required = data.is_required ?? false
        this.isPublic = data.is_public ?? false
        this.client = data.is_client ?? false
        this.booking = data.is_booking ?? false
        this.location = data.is_location ?? false
        this.order = data.is_order ?? false
        this.subaccount = data.is_subaccount ?? false
        this.checkoutOther = data.checkout_other ?? false
        this.checkoutOrder = data.checkout_order ?? false
        this.checkoutClient = data.checkout_client ?? false
        this.checkoutBooking = data.checkout_booking ?? false
        this.checkoutLocation = data.checkout_location ?? false
        this.type = data.type ?? undefined
        this.name = data.name ?? undefined
        this.expires = data.expires ?? undefined
        this.standardField = data.standard_field ?? undefined
        this.label = data.label ?? undefined
        this.seqnum = this.propertyFromIntArg(data.seqnum)
        this.edition = data.edition ?? undefined
        this.editionField = data.edition_field ?? undefined
        this.options = (() => {
            const options: Option[] = []

            if (data.options !== undefined && data.options !== null) {
                for (const optionData of data.options ?? []) {
                    if (optionData instanceof Option) {
                        options.push(optionData)
                        continue
                    }
                    options.push(
                        new Option(undefined, {
                            id: optionData['value'],
                            label: optionData['label'],
                            seqnum: optionData['seqnum'],
                        })
                    )
                }
            }

            if (options.length === 0 && data.labels) {
                let seqnum = 0
                for (const label of data.labels) {
                    options.push(new Option(undefined, { id: '', label, seqnum }))
                    seqnum++
                }
            }

            options.sort((a, b) => a.compareTo(b))
            return options
        })()
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<FieldConstructorArgs>): Field {
        return new Field({
            ...this.serializeForClone(),
            is_required: this.required,
            is_client: this.client,
            is_booking: this.booking,
            is_location: this.location,
            is_order: this.order,
            is_subaccount: this.subaccount,
            ...overrides,
        })
    }

    get allowEdit(): boolean {
        return (
            !this.standardField ||
            this.subaccount ||
            !['name', 'phone', 'email'].includes(this.name ?? '')
        )
    }

    get allowExpires(): boolean {
        return !!(
            (this.type === 'document' || this.type === 'image') &&
            (this.client || this.subaccount) &&
            !this.booking &&
            !this.order &&
            !this.record &&
            !this.standardField
        )
    }

    static isField(
        name: string | undefined,
        owner: string | undefined,
        record: string | undefined
    ): boolean {
        return name !== undefined && name.startsWith('custom_') && !!Field.type(name, owner, record)
    }

    static type(
        name: string | undefined,
        owner: string | undefined,
        record: string | undefined
    ): string | undefined {
        let type: string | undefined
        if (name != undefined && name.startsWith('custom_') && owner != undefined) {
            if (name.endsWith('_result')) {
                // handle image custom field metadata
                const resultName = name.substring(0, name.length - '_result'.length)
                type = Field.type(resultName, owner, record)
                if (type === 'image' || type === 'document') return undefined
            }
            const cacheKey = Field.cacheKey(name, owner, record)
            const cachedValue = CacheFactory.getInstance().objectForKey<string>(cacheKey)

            if (cachedValue) {
                type = cachedValue
            }

            // fields are always available in cache (see RecordFactory.initializeCache), no need to call loadFields here
        }
        return type
    }

    static cacheKey(name: string, owner: string, record?: string): string {
        return record
            ? `${Field.FIELD_CACHE_KEY_PREFIX}.${name}.${owner}.${record}`
            : `${Field.FIELD_CACHE_KEY_PREFIX}.${name}.${owner}`
    }

    static expirationStrings(): Option[] {
        return [
            new Option(undefined, { id: '', label: 'Never expires' }),
            new Option(undefined, {
                id: 'date',
                label: 'On specific date',
                group: undefined,
                subtitle:
                    'You and the client are able to set the expiration date when uploading a file',
            }),
            new Option(undefined, { id: '1m', label: 'Every month' }),
            new Option(undefined, { id: '2m', label: 'Every 2 months' }),
            new Option(undefined, { id: '3m', label: 'Every 3 months' }),
            new Option(undefined, { id: '6m', label: 'Every 6 months' }),
            new Option(undefined, { id: '1y', label: 'Every 12 months' }),
            new Option(undefined, { id: '18m', label: 'Every 18 months' }),
            new Option(undefined, { id: '2y', label: 'Every 2 years' }),
            new Option(undefined, { id: '3y', label: 'Every 3 years' }),
        ]
    }

    static async storeFieldData(
        recordFactory: RecordFactory,
        recordData: RecordDataMap,
        owner: string,
        record?: string
    ): Promise<Image[]> {
        const l: Image[] = []

        const d = recordData.hasValueForKey('field_values')
            ? recordData.valueForDictionaryKey('field_values')
            : new RecordDataMap()

        if (!!d) {
            for (let key of Object.keys(d)) {
                const type = Field.type(key, owner, record)

                if (!type) continue

                if ((type === 'image' || type === 'document') && d.hasValueForKey(key)) {
                    const file = d.valueForDictionaryKey(`${key}_result`)

                    if (file && file.hasValueForKey('id')) {
                        l.push(await recordFactory.storeImage(file, true))
                    }
                }
            }
        }
        return l
    }

    static serializeFieldData(
        fieldData: RecordDataMap,
        owner: string | undefined,
        record: string | undefined
    ): RecordDataMap {
        const postData = new RecordDataMap()
        for (let key of Object.keys(fieldData)) {
            const type = Field.type(key, owner, record)
            if (!type) continue
            const value = Field.valueForField(fieldData, key, type)
            postData.put(key, value)
            if ((type === 'image' || type === 'document') && value != undefined) {
                const image = value
                postData.put(key, image.id)
            }
        }
        return postData
    }

    static valueForField(fieldValues: RecordDataMap, name: string, type: string): any {
        if (fieldValues != undefined) {
            if (type === 'date') return fieldValues.valueForDateKey(name)
            else if (type === 'integer') return fieldValues.valueForIntKey(name)
            else if (type === 'checkbox') {
                const value = fieldValues.valueForKey(name)
                return typeof value === 'boolean' ? value : value === 'true'
            }
            else if (type === 'currency') return fieldValues.valueForCurrencyKey(name)
            else if (type === 'float') return fieldValues.valueForNumberKey(name)
            else if (type === 'image' || type === 'document')
                return fieldValues.hasValueForKey(name) &&
                    fieldValues.hasValueForKey(`${name}_result`)
                    ? new Image(undefined, {
                          id: fieldValues.valueForKey(name),
                          uri: fieldValues
                              .valueForDictionaryKey(`${name}_result`)!
                              .valueForKey('uri'),
                      })
                    : undefined
            else if (type === 'multiselect') return fieldValues.valueForListKey(name)
            return fieldValues.valueForKey(name)
        }
        return undefined
    }

    static loadFromRecordMap(
        values?: RecordDataMap,
        owner?: string,
        record?: string
    ): RecordDataMap {
        const newDict = new RecordDataMap()
        const d =
            values !== undefined && values.hasValueForKey('field_values')
                ? values.valueForDictionaryKey('field_values')
                : values
        if (!!d) {
            for (const key of Object.keys(d)) {
                const type = Field.type(key, owner, record)
                if (!type) continue
                newDict.put(key, Field.valueForField(d, key, type))
            }
        }
        return newDict
    }

    static loadFieldData(fields: Field[], values: RecordDataMap): RecordDataMap[] {
        const fieldArray: RecordDataMap[] = []
        for (const field of fields) {
            if (!field.name) continue
            const fieldValue = values.get(field.name)
            const fieldInfo = new RecordDataMap()
            fieldInfo.put('field', field)
            fieldInfo.put('value', fieldValue)
            const type = field.type
            if (
                fieldValue === undefined ||
                (typeof fieldValue === 'string' && fieldValue.length === 0)
            )
                continue
            if (!fieldValue?.length) continue
            if (type === 'text' || type === 'email' || type === 'longtext') {
                fieldInfo.put('display', fieldValue)
            }
            else if (type === 'float') {
                const floatValue =
                    typeof fieldValue === 'number'
                        ? fieldValue
                        : typeof fieldValue === 'string'
                          ? parseFloat(fieldValue)
                          : values.valueForNumberKey(field.name)
                fieldInfo.put('display', fieldValue)
            }
            else if (type === 'integer') {
                const intValue =
                    typeof fieldValue === 'number'
                        ? fieldValue
                        : typeof fieldValue === 'string'
                          ? parseInt(fieldValue)
                          : values.valueForIntKey(field.name)
                fieldInfo.put('display', fieldValue)
            }
            else if (type === 'checkbox') {
                const boolValue = values.valueForBoolKey(field.name)
                fieldInfo.put('display', boolValue ? 'Yes' : 'No')
            }
            else if (type === 'date') {
                const dateValue = moment.isMoment(fieldValue)
                    ? fieldValue
                    : typeof fieldValue === 'string'
                      ? values.valueForDateKey(field.name)
                      : undefined
                if (dateValue) fieldInfo.put('display', dateValue.format('MMM D, YYYY'))
            }
            else if (type === 'phone') {
                fieldInfo.put('display', formatPhoneNumber(fieldValue.toString()))
            }
            else if (type === 'currency') {
                fieldInfo.put('display', currencyFormat(fieldValue))
            }
            else if (type === 'select' || type === 'multiselect') {
                const lblArray: string[] = []
                if (fieldValue instanceof BaseRecord) lblArray.push(fieldValue.toString())
                else {
                    const l: string[] = []
                    if (fieldValue instanceof Array) {
                        l.push(...fieldValue)
                    }
                    else {
                        l.push(fieldValue.toString())
                    }
                    for (let option of field.options) {
                        if (l.includes(option.id ?? '')) {
                            lblArray.push(option.label!)
                        }
                    }
                }
                fieldInfo.put('display', lblArray)
            }
            fieldArray.push(fieldInfo)
        }
        return fieldArray
    }

    static valuesAsString(fields: RecordDataMap[]): string | undefined {
        if (!fields || !fields.length) {
            return undefined
        }
        const l: string[] = []
        for (let info of fields) {
            let display = info.get('display')
            let stringDisplay = ''
            if (display instanceof Array) {
                stringDisplay = display.join(', ')
            }
            else if (display instanceof Number) {
                stringDisplay = display.toString()
            }
            else if (display instanceof Boolean) {
                stringDisplay = display ? 'Yes' : 'No'
            }
            else if (typeof display === 'string') {
                stringDisplay = display
            }
            if (stringDisplay) {
                l.push(stringDisplay)
            }
        }
        return l.join(', ')
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.record) this.record = BaseRecord.fromSerializer(data.record, Type)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.service) this.service = BaseRecord.fromSerializer(data.service, Industry)
        if (data.options)
            this.options = data.options.map((option: any) =>
                BaseRecord.fromSerializer(option, Option)
            ) //todo: check this line
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'creator'],
        types: ['record'],
    }

    static dataType(): DataType {
        return 'fields'
    }

    jsonObject(): any {
        const dict: any = super.jsonObject()
        dict['type'] = this.type
        dict['label'] = this.label
        dict['expires'] = this.expires
        if (this.record !== undefined) dict['record'] = this.record.id
        dict['is_client'] = this.client ? 'true' : 'false'
        dict['is_booking'] = this.booking ? 'true' : 'false'
        dict['is_order'] = this.order ? 'true' : 'false'
        dict['is_subaccount'] = this.subaccount ? 'true' : 'false'
        dict['is_required'] = this.required ? 'true' : 'false'
        dict['is_public'] = this.isPublic ? 'true' : 'false'
        dict['checkout_other'] = this.checkoutOther ? 'true' : 'false'
        dict['checkout_order'] = this.checkoutOrder ? 'true' : 'false'
        dict['checkout_client'] = this.checkoutClient ? 'true' : 'false'
        dict['checkout_booking'] = this.checkoutBooking ? 'true' : 'false'
        if (this.type?.endsWith('select')) {
            const l: string[] = []
            const vl: string[] = []
            for (const option of this.options) {
                l.push(option.label ?? '')
                vl.push(option.id ?? '')
            }
            dict['labels'] = l
            dict['values'] = vl
        }
        if (!!this.edition) dict['edition'] = this.edition
        if (!!this.editionField) dict['edition_field'] = this.editionField
        return dict
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get record(): Type | undefined {
        return this.getForeignRecord('record', Type)
    }
    set record(record: Type | undefined) {
        this.setForeignRecord('record', record)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get service(): Industry | undefined {
        return this.getForeignRecord('service', Industry)
    }
    set service(service: Industry | undefined) {
        this.setForeignRecord('service', service)
    }
}
